import React, { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ActivityButtonGroup.scss';
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, CustomInput
} from 'reactstrap';
import moment from 'moment';
import plusIcon from '../../images/icn-plus.svg';
import ActivityType from '../ActivityType';
import ModalConfirm from '../ModalConfirm';
import editIcon from '../../images/icn-edit.svg';
import { useAuth } from '../Auth';
import { isAdminRole } from '../../constants/user';
import { fetchUsers } from '../../lib/fetch/users';
import { ButonTrash } from '../ButtonTrash/ButtonTrash';
import DateInput from '../DateInput';
import { activityPriorities } from '../../constants/activities';
import { fetchContacts } from '../../lib/fetch/contacts';
import { default as fetchDeals } from '../../lib/fetch/deals';

function ActivityModal({ open, toggle, onSubmit, activity, user, type, onDeleteActivity, dealId, contactId, showDealSelect = false, showContactSelect = false }) {


  const [t] = useTranslation('ACTIVITIES');
  const [tCommon] = useTranslation('COMMON');
  const { id } = activity || {};

  const [{ token, profile = {} } = {}] = useAuth();
  const { role } = profile;
  const isAdmin = isAdminRole(role);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [validationError, setValidationError] = useState('');

  const [form, setForm] = useState({
    user_id: user ?? activity?.user_id ?? null,
    done: activity?.done === 1 || activity?.done === true,
    description: activity?.description,
    date: moment(activity?.date).format('YYYY-MM-DD'),
    time: moment(activity?.date).format('HH:mm'),
    priority: activity?.priority ?? activityPriorities.LOW.value,
    deal_id: dealId ? parseInt(dealId, 10) : (activity?.deal_id ? parseInt(activity.deal_id, 10) : null),
    contact_id: contactId ? parseInt(contactId, 10) : (activity?.contact_id ? parseInt(activity.contact_id, 10) : null)
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setForm({
        user_id: user ?? activity?.user_id ?? null,
        done: activity?.done === 1 || activity?.done === true,
        description: activity?.description,
        date: moment(activity?.date).format('YYYY-MM-DD'),
        time: moment(activity?.date).format('HH:mm'),
        priority: activity?.priority ?? activityPriorities.LOW.value,
        deal_id: dealId ? parseInt(dealId, 10) : (activity?.deal_id ? parseInt(activity.deal_id, 10) : null),
        contact_id: contactId ? parseInt(contactId, 10) : (activity?.contact_id ? parseInt(activity.contact_id, 10) : null)
      });
      setValidationError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?.id, dealId, contactId]);

  const updateValue = (name, value) => {
    setForm({ ...form, [name]: value });
  }

  const fetchOptions = useCallback(async () => {
    if (token) {
      try {
        const { data: usersData } = await fetchUsers({ token, limit: 1000 });
        setUsers(usersData);

        if (showContactSelect) {
          const { data: contactsData } = await fetchContacts({ token, limit: 1000 });
          setContacts(contactsData);
        }

        if (showDealSelect) {
          const { data: dealsData } = await fetchDeals({ token, limit: 1000 });
          setDeals(dealsData?.deals || []);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    }
  }, [token, showContactSelect, showDealSelect]);

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, [token]);

  const prepareData = (e) => {
    e.preventDefault();

    // Validazione: almeno uno tra deal_id e contact_id deve essere selezionato
    if (showDealSelect || showContactSelect) {
      const hasDeal = form.deal_id && form.deal_id !== 0;
      const hasContact = form.contact_id && form.contact_id !== 0;

      if (!hasDeal && !hasContact) {
        setValidationError(tCommon('SELECT_AT_LEAST_DEAL_OR_CONTACT'));
        return;
      }
    }

    setValidationError('');
    const data = {
      ...form,
      done: form.done ? 1 : 0,
      deal_id: form.deal_id || null,
      contact_id: form.contact_id || null
    };
    onSubmit(data);
  };

  return (
    <Modal isOpen={open} fade={false} toggle={toggle} zIndex={2000}>
      <ModalHeader toggle={toggle}>
        {t(id ? 'UPDATE_ACTIVITY' : 'REGISTER_NEW_ACTIVITY')} <br />
        <ActivityType type={type} />
      </ModalHeader>
      <ModalBody>
        <Form inline className="m-b-30" onSubmit={prepareData}>
          <FormGroup className="mb-4" style={{ width: '50%' }}>
            <Label for="Date" className="mr-2">{t('DAY')}:</Label>
            <DateInput
              name="date"
              disablePopup={true}
              value={form.date}
              onChange={value => updateValue('date', value)}
            />
          </FormGroup>
          <FormGroup className="mb-4" style={{ width: '50%' }}>
            <Label for="Time" className="">{t('HOURS')}:</Label>
            <Input
              type="time"
              name="time"
              className="mr-4"
              value={form.time}
              onChange={(e) => {
                const value = e.target.value;
                updateValue('time', value);
              }}
            />
          </FormGroup>

          <FormGroup className="mb-4" style={{ width: '50%' }}>
            <Label className="mr-2">{t('COMMON:USER')}</Label>
            <CustomInput
              type="select"
              name="user_id"
              id="user_id"
              value={form?.user_id}
              onChange={e => {
                const value = e.target.value;
                updateValue('user_id', value);
              }}
              disabled={!isAdmin}
            >
              <option value={0}>{tCommon('NO_VALUE_SELECTED')}</option>
              {
                users?.map(({ id, first_name, last_name }) => (
                  <option value={id} key={id}>{first_name} {last_name}</option>
                ))
              }
            </CustomInput>
          </FormGroup>

          <FormGroup className="mb-4" style={{ width: '50%' }}>
            <Label className="mr-2">{t('COMMON:PRIORITY')}</Label>
            <CustomInput
              type="select"
              name="priority"
              id="priority"
              value={form?.priority}
              onChange={e => {
                const value = parseInt(e.target.value, 10);
                updateValue('priority', value);
              }}
            >
              {Object.values(activityPriorities).map(({ value, label }) => (
                <option key={value} value={value}>{tCommon(label)}</option>
              ))}
            </CustomInput>
          </FormGroup>

          {showContactSelect && (
            <FormGroup className="mb-4" style={{ width: '50%' }}>
              <Label className="mr-2">{t('COMMON:CONTACT')}</Label>
              <CustomInput
                type="select"
                name="contact_id"
                id="contact_id"
                value={form?.contact_id || 0}
                onChange={e => {
                  const value = parseInt(e.target.value, 10);
                  setValidationError('');
                  setForm(prevForm => ({
                    ...prevForm,
                    contact_id: value === 0 ? null : value
                  }));
                }}
              >
                <option value={0}>{tCommon('NO_VALUE_SELECTED')}</option>
                {
                  contacts && Array.isArray(contacts) && contacts.map(({ id, first_name, last_name }) => (
                    <option value={id} key={id}>{first_name} {last_name}</option>
                  ))
                }
              </CustomInput>
            </FormGroup>
          )}

          {showDealSelect && (
            <FormGroup className="mb-4" style={{ width: '50%' }}>
              <Label className="mr-2">{t('COMMON:DEALS')}</Label>
              <CustomInput
                type="select"
                name="deal_id"
                id="deal_id"
                value={form?.deal_id || 0}
                onChange={e => {
                  const value = parseInt(e.target.value, 10);
                  setValidationError('');
                  setForm(prevForm => ({
                    ...prevForm,
                    deal_id: value === 0 ? null : value
                  }));
                }}
              >
                <option value={0}>{tCommon('NO_VALUE_SELECTED')}</option>
                {
                  deals && Array.isArray(deals) && deals.map(({ id, name }) => (
                    <option value={id} key={id}>{name}</option>
                  ))
                }
              </CustomInput>
            </FormGroup>
          )}

          {(showDealSelect || showContactSelect) && validationError && (
            <FormGroup className="mb-4" style={{ width: '100%' }}>
              <div style={{ color: '#dc3545', fontSize: '0.875rem', fontWeight: '500' }}>
                {validationError}
              </div>
            </FormGroup>
          )}

          <FormGroup className="mb-4" style={{ width: '50%' }}>
            <CustomInput
              key={`done-${id || 'new'}`}
              className="done-checkbox"
              name="done"
              checked={!!form.done}
              type="checkbox"
              id={`done-${id || 'new'}`}
              onChange={(e) => {
                const checked = e.target.checked;
                setForm(prevForm => ({ ...prevForm, done: checked }));
              }}
              label={t("COMMON:SET_AS_DONE")}
            />
          </FormGroup>

          <FormGroup className="mb-4 form-description">
            <Label className="mr-2">{t('COMMON:DESCRIPTION')}</Label>
            <Input
              type="textarea"
              name="description"
              className="mx-sm-4"
              value={form.description || ''}
              onChange={(e) => {
                const value = e.target.value;
                updateValue('description', value);
              }}
            />
          </FormGroup>

          <ModalFooter className="cont-btn-modalform">
            {
              id && typeof onDeleteActivity === 'function' &&
              <ModalConfirm
                text={t('DELETE_REQUEST')}
                onConfirm={onDeleteActivity}
                className="user-delete-button"
              >
                <ButonTrash />
              </ModalConfirm>
            }
            <Button type="submit" color="primary">
              <img src={id ? editIcon : plusIcon} alt="" />
              {t('SAVE_ACTIVITY')}
            </Button>
            <Button color="secondary" onClick={toggle}>{t('COMMON:CANCEL')}</Button>
          </ModalFooter>
        </Form >
      </ModalBody >
    </Modal >
  );
}

export default ActivityModal;
